import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Inbox,
  Send,
  CheckCircle2,
  Loader2,
  RefreshCcw,
  Filter,
} from "lucide-react";
import { toast } from "sonner";

/**
 * Admin tab showing wanted-profile requests (categories with 0 photos that
 * users requested). Aggregates per-category counts at the top + recent
 * requests list below with one-click resolve.
 */
export default function WantedProfilesTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("unresolved"); // unresolved | all | resolved
  const [pendingId, setPendingId] = useState(null);
  const [pendingCategory, setPendingCategory] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter === "unresolved") params.resolved = false;
      if (filter === "resolved") params.resolved = true;
      const { data: resp } = await api.get("/admin/wanted-profiles", { params });
      setData(resp);
    } catch (e) {
      toast.error(
        formatApiErrorDetail(e.response?.data?.detail) || "Eroare la încărcare",
      );
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    load();
  }, [load]);

  const resolveOne = async (req) => {
    setPendingId(req.id);
    try {
      await api.post(`/admin/wanted-profiles/${req.id}/resolve`);
      toast.success("Marcat ca rezolvat");
      await load();
    } catch (e) {
      toast.error(
        formatApiErrorDetail(e.response?.data?.detail) || "Eroare",
      );
    } finally {
      setPendingId(null);
    }
  };

  const resolveByCategory = async (cat) => {
    const key = `${cat.sim_type}::${cat.category_slug}`;
    setPendingCategory(key);
    try {
      const { data: resp } = await api.post(
        "/admin/wanted-profiles/resolve-by-category",
        { sim_type: cat.sim_type, category_slug: cat.category_slug },
      );
      toast.success(
        `${resp.resolved_count} solicitări marcate ca rezolvate pentru "${cat.category_name}"`,
      );
      await load();
    } catch (e) {
      toast.error(
        formatApiErrorDetail(e.response?.data?.detail) || "Eroare",
      );
    } finally {
      setPendingCategory(null);
    }
  };

  if (loading && !data) {
    return (
      <div className="py-20 flex items-center justify-center text-zinc-500">
        <Loader2 className="animate-spin mr-2" size={18} />
        Se încarcă...
      </div>
    );
  }

  const items = data?.items || [];
  const byCat = data?.by_category || [];

  return (
    <div className="space-y-6" data-testid="wanted-profiles-tab">
      {/* Header */}
      <div className="rounded-xl border border-white/10 bg-zinc-900/30 p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h2 className="text-xl font-semibold text-white mb-1 flex items-center gap-2">
              <Inbox className="text-orange-400" size={20} />
              Solicitări profile lipsă
            </h2>
            <p className="text-sm text-zinc-400 leading-relaxed max-w-2xl">
              Clienții au cerut profile pentru aceste categorii încă nepopulate
              în catalog. Folosește-le pentru a prioritiza ce să adaugi
              următor: categoria cu cele mai multe solicitări = oportunitate
              de business validată.
            </p>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <div className="rounded-md border border-orange-500/30 bg-orange-500/5 px-3 py-1.5">
              <span className="text-orange-400 font-bold">{data?.unresolved || 0}</span>
              <span className="text-zinc-400 ml-1.5 text-xs uppercase tracking-wider">
                nerezolvate
              </span>
            </div>
            <div className="rounded-md border border-white/10 bg-zinc-900/40 px-3 py-1.5">
              <span className="text-zinc-300 font-bold">{data?.total || 0}</span>
              <span className="text-zinc-500 ml-1.5 text-xs uppercase tracking-wider">
                total
              </span>
            </div>
            <button
              onClick={load}
              className="p-2 rounded-md border border-white/10 hover:bg-white/5 text-zinc-300"
              data-testid="wanted-refresh"
              title="Reîncarcă"
            >
              <RefreshCcw size={14} />
            </button>
          </div>
        </div>
      </div>

      {/* Aggregate by category */}
      <div className="rounded-xl border border-white/10 bg-zinc-900/30 p-5">
        <h3 className="font-outfit font-bold text-white text-base mb-4 flex items-center gap-2">
          <Filter size={16} className="text-orange-400" />
          Top categorii cerute (nerezolvate)
        </h3>
        {byCat.length === 0 ? (
          <p className="text-sm text-zinc-500 italic">
            Nicio categorie cu solicitări nerezolvate. Excelent!
          </p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {byCat.map((cat) => {
              const catKey = `${cat.sim_type}::${cat.category_slug}`;
              const pending = pendingCategory === catKey;
              return (
                <div
                  key={catKey}
                  className="border border-orange-500/20 bg-orange-500/5 rounded-lg p-4"
                  data-testid={`wanted-cat-${cat.category_slug}`}
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div>
                      <div className="font-outfit font-bold text-white text-sm">
                        {cat.category_name}
                      </div>
                      <div className="text-[10px] uppercase tracking-wider text-zinc-500 mt-0.5">
                        {cat.sim_type === "INTERIOR" ? "Interior" : "Exterior"}
                      </div>
                    </div>
                    <div className="text-2xl font-black font-outfit text-orange-400 leading-none">
                      {cat.count}
                    </div>
                  </div>
                  <div className="text-[10px] text-zinc-500 mb-3">
                    Ultima cerere:{" "}
                    {cat.last_seen
                      ? new Date(cat.last_seen).toLocaleDateString("ro-RO", {
                          day: "2-digit",
                          month: "short",
                          hour: "2-digit",
                          minute: "2-digit",
                        })
                      : "—"}
                  </div>
                  <Button
                    size="sm"
                    onClick={() => resolveByCategory(cat)}
                    disabled={pending}
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
                    data-testid={`wanted-cat-resolve-${cat.category_slug}`}
                  >
                    {pending ? (
                      <>
                        <Loader2 size={12} className="mr-1.5 animate-spin" />
                        Se procesează...
                      </>
                    ) : (
                      <>
                        <CheckCircle2 size={12} className="mr-1.5" />
                        Marchează rezolvat ({cat.count})
                      </>
                    )}
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Recent requests list */}
      <div className="rounded-xl border border-white/10 bg-zinc-900/30 p-5">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <h3 className="font-outfit font-bold text-white text-base flex items-center gap-2">
            <Send size={16} className="text-orange-400" />
            Solicitări individuale
          </h3>
          <div className="flex items-center gap-1 text-xs">
            {[
              { id: "unresolved", label: "Nerezolvate" },
              { id: "resolved", label: "Rezolvate" },
              { id: "all", label: "Toate" },
            ].map((f) => (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                className={`px-3 py-1 rounded-md transition ${
                  filter === f.id
                    ? "bg-orange-500 text-white"
                    : "bg-zinc-900/60 text-zinc-400 hover:text-white"
                }`}
                data-testid={`wanted-filter-${f.id}`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {items.length === 0 ? (
          <p className="text-sm text-zinc-500 italic py-6 text-center">
            Nicio solicitare în acest filtru.
          </p>
        ) : (
          <div className="space-y-2">
            {items.map((req) => (
              <div
                key={req.id}
                className={`rounded-lg border p-3 ${
                  req.resolved
                    ? "border-emerald-500/20 bg-emerald-500/5"
                    : "border-white/10 bg-zinc-950/40"
                }`}
                data-testid={`wanted-item-${req.id}`}
              >
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1.5">
                      <span className="font-outfit font-bold text-white text-sm">
                        {req.category_name}
                      </span>
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-800/80 text-zinc-400 uppercase tracking-wider">
                        {req.sim_type === "INTERIOR" ? "Interior" : "Exterior"}
                      </span>
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-800/80 text-zinc-400 uppercase tracking-wider">
                        {req.user_role}
                      </span>
                      {req.resolved && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300 uppercase tracking-wider flex items-center gap-1">
                          <CheckCircle2 size={9} /> Rezolvat
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-zinc-500 mb-2">
                      {req.user_name && (
                        <span className="text-zinc-300">{req.user_name}</span>
                      )}{" "}
                      · {req.user_email} ·{" "}
                      {new Date(req.created_at).toLocaleString("ro-RO", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </div>
                    {req.message ? (
                      <p className="text-sm text-zinc-300 leading-relaxed bg-zinc-950/60 border border-white/5 rounded px-3 py-2 mt-1">
                        {req.message}
                      </p>
                    ) : (
                      <p className="text-xs text-zinc-600 italic">(fără mesaj)</p>
                    )}
                    {req.resolved && req.resolved_by && (
                      <p className="text-[10px] text-emerald-500/80 mt-1.5">
                        Rezolvat de {req.resolved_by} pe{" "}
                        {new Date(req.resolved_at).toLocaleDateString("ro-RO")}
                      </p>
                    )}
                  </div>
                  {!req.resolved && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => resolveOne(req)}
                      disabled={pendingId === req.id}
                      className="border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10 text-xs shrink-0"
                      data-testid={`wanted-resolve-${req.id}`}
                    >
                      {pendingId === req.id ? (
                        <Loader2 size={12} className="animate-spin" />
                      ) : (
                        <>
                          <CheckCircle2 size={12} className="mr-1" />
                          Rezolvă
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
